import * as React from "react";
import clsx from "clsx";
import { Text } from "../../../ui/components/Text";
import type { SetupTask } from "../../lib/auth/user-setup-progress";
import { SETUP_TASK_ORDER } from "../../lib/auth/user-setup-progress";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import { useI18n } from "../../lib/i18n";

const TASK_ICON_SIZE = 44;

const TASK_ICONS: Record<SetupTask, () => React.JSX.Element> = {
  email: () => (
    <svg
      width={TASK_ICON_SIZE}
      height={TASK_ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  phone: () => (
    <svg
      width={TASK_ICON_SIZE}
      height={TASK_ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  organization: () => (
    <svg
      width={TASK_ICON_SIZE}
      height={TASK_ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      aria-hidden
    >
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
      <path d="M9 9v0" />
      <path d="M9 12v0" />
      <path d="M9 15v0" />
      <path d="M9 18v0" />
    </svg>
  ),
};

const TASK_LABEL_KEYS: Record<SetupTask, string> = {
  email: "account.setup.nav.taskEmail",
  phone: "account.setup.nav.taskPhone",
  organization: "account.setup.nav.taskOrganization",
};

function StatusBadgeIcon({ complete }: { complete: boolean }) {
  if (complete) {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
        <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function UserSetupNavigation({
  activeTask,
  progressPercent,
  isTaskComplete,
  onSelectTask,
}: {
  activeTask: SetupTask;
  progressPercent: number;
  isTaskComplete: (task: SetupTask) => boolean;
  onSelectTask: (task: SetupTask) => void;
}) {
  const { t, tx } = useI18n();

  return (
    <div className={authText.setupNavHeader}>
      <Text level="h3" weight="bold" style={{ margin: "0 0 12px" }}>
        {tx("account.setup.nav.heading")}
      </Text>
      <div className={authText.setupTabList} role="tablist" aria-label={t("account.setup.nav.ariaLabel")}>
        {SETUP_TASK_ORDER.map((task) => {
          const Icon = TASK_ICONS[task];
          const complete = isTaskComplete(task);
          const isActive = activeTask === task;
          return (
            <button
              key={task}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`user-setup-panel-${task}`}
              id={`user-setup-tab-${task}`}
              className={authText.setupTab}
              onClick={() => onSelectTask(task)}
            >
              <div
                className={clsx(
                  authText.setupTabIconRing,
                  isActive && authText.setupTabIconRingActive,
                )}
              >
                <div className={clsx(authText.setupTabIcon, isActive && authText.setupTabIconActive)}>
                  <Icon />
                </div>
                <span
                  className={clsx(
                    authText.setupTabStatusBadge,
                    complete ? authText.setupTabStatusComplete : authText.setupTabStatusPending,
                  )}
                  aria-label={complete ? t("account.setup.nav.statusComplete") : t("account.setup.nav.statusPending")}
                >
                  <StatusBadgeIcon complete={complete} />
                </span>
              </div>
              <span
                className={clsx(authText.setupTabLabel, isActive && authText.setupTabLabelActive)}
              >
                {tx(TASK_LABEL_KEYS[task])}
              </span>
            </button>
          );
        })}
      </div>
      <div
        className={authText.setupProgressTrack}
        role="progressbar"
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("account.setup.nav.progressAriaLabel")}
        style={{ marginTop: 12 }}
      >
        <div
          className={authText.setupProgressFill}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}
